import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { IDataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import {
    IQueryContextChange,
    IQueryContextDatasourceColumnState,
} from 'src/app/services/query-context/models/IQueryContext';
import {
    QueryContextChange,
    QueryContextService,
} from 'src/app/services/query-context/query-context.service';
import { compareArrays } from 'src/app/utils/array';
import { IDatasourceColumnDashboardItem } from 'src/app/utils/dashboard-item';
import {
    DatasourceColumnId,
    getDatasourceColumnName,
} from 'src/app/utils/datasource-sql-map/datasource';
import { selectedValuesPill } from 'src/app/utils/ng-zorro';

export interface IMultipleSelectDataLoaderResult extends IDataLoaderResult {
    data: Array<{ value: any; label: string }>;
}
export type SelectMode = 'single' | 'multiple';

const ALL_VALUES_OPTION = { value: 'ALL', label: 'All' };

export interface IMultipleSelectDatasourceColumnState
    extends IQueryContextDatasourceColumnState {
    value: {
        options: any[];
        filterType: 'include' | 'exclude';
    };
}

/** Interface for the multiple select dashboard item */
export interface IMultipleSelectDashboardItem
    extends IDatasourceColumnDashboardItem {
    /** True if filter can be set to exclude mode */
    excludeMode?: boolean;
    /** True if filter can be split */
    splitable?: boolean;
    datasourceColumn: {
        id: DatasourceColumnId;
        state: IMultipleSelectDatasourceColumnState;
    };
    /**
     * Show the navigation '<' and '>' buttons
     * @default false
     */
    showQuickSelectionButtons?: boolean;
}

type OptionChange =
    | { changeType: 'add' | 'remove' | 'replace'; value: any }
    | undefined;

/**
 * Returns the default state for the multiple select datasource column.
 *
 * @returns {IMultipleSelectDatasourceColumnState} The default state object containing:
 * - `value`: An object with `options` as an empty array and `filterType` set to 'include'.
 * - `isFilterApplied`: A boolean indicating if a filter is applied, default is false.
 * - `isSplitApplied`: A boolean indicating if a split is applied, default is false.
 */
function getDefaultState(): IMultipleSelectDatasourceColumnState {
    return {
        value: { options: [], filterType: 'include' },
        isFilterApplied: false,
        isSplitApplied: false,
    };
}

// TODO dodać obsługę single choice
@Component({
    selector: 'app-multiple-select',
    templateUrl: './multiple-select.component.html',
    styleUrls: ['./multiple-select.component.scss'],
})
export class MultipleSelectComponent implements OnInit {
    @Input()
    datasourceColumnId!: DatasourceColumnId;

    @Input()
    excludeMode: boolean = true;

    @Input()
    splitable: boolean = true;

    @Input()
    showQuickSelectionButtons: boolean = false;

    @Input()
    selectMode: SelectMode = 'multiple';
    get nzMode() {
        return this.selectMode == 'single' ? 'default' : 'multiple';
    }

    state: IMultipleSelectDatasourceColumnState = getDefaultState();

    @Input()
    set dataLoaderData(dataLoaderData: IMultipleSelectDataLoaderResult) {
        // console.log(
        //     `MultipleSelectComponent.${this.datasourceColumnId}.set.dataLoaderData`
        // );
        this._dataLoaderData = dataLoaderData;
        this.availableOptions = dataLoaderData.data.slice();
        this.availableViewOptions =
            this.availableOptions.length > 0
                ? [ALL_VALUES_OPTION, ...dataLoaderData.data.slice()]
                : [];
    }
    // Raw data from the DataLoader
    _dataLoaderData!: IMultipleSelectDataLoaderResult; // = {value: []};

    // Available options set by the DataLoader
    availableOptions: IMultipleSelectDataLoaderResult['data'] = [];
    availableViewOptions: IMultipleSelectDataLoaderResult['data'] = [];

    // Selected options
    selectedOptions: any[] = [];
    // View model for the selected options (added 'all' option)
    private _selectedOptionsViewModel: any[] = [];
    get selectedOptionsViewModel(): any[] | string {
        if (this.selectMode == 'single') {
            return this._selectedOptionsViewModel[0];
        }
        return this._selectedOptionsViewModel;
    }
    set selectedOptionsViewModel(options: any[] | string) {
        if (typeof options === 'string') {
            this._selectedOptionsViewModel = [options];
        } else {
            this._selectedOptionsViewModel = options;
        }
    }
    // Previous selected view options model
    previousSelectedViewOptionsModel: any[] = [];
    // Is exclude checked
    excludeChecked: boolean = false;
    // Is split checked
    splitChecked: boolean = false;

    // General rules for disabling buttons
    get quickSelectionButtonsDisabled(): boolean {
        return (
            !this.availableOptions.length ||
            this.allValuesSelected ||
            this.selectedOptions.length !== 1 ||
            this.currentOptionIndex() === -1
        );
    }
    // Rules for disabling the previous button
    get prevDisabled(): boolean {
        if (this.quickSelectionButtonsDisabled) return true;
        return this.currentOptionIndex() <= 0;
    }
    // Rules for disabling the next button
    get nextDisabled(): boolean {
        if (this.quickSelectionButtonsDisabled) return true;
        return this.currentOptionIndex() >= this.availableOptions.length - 1;
    }

    // Are all values selected
    allValuesSelected: boolean = false;

    // Filter type
    selectedFilterTypeModel: 'include' | 'exclude' = 'include';

    /** flag which indicates whether the multi-select panel is opened */
    private isOpened: boolean = false;

    /** temporary variable loaded every time a dropdown is opened */
    private selectedBeforeDropdownOpen: string[] = [];

    constructor(
        private queryContextService: QueryContextService,
        private cd: ChangeDetectorRef
    ) {}

    /** React to changes in the query context */
    queryContextSubscriber = (contextChange: QueryContextChange) => {
        if (contextChange.reason === 'InitialContext') {
            // find in contextChange.context.datasourceColumns item with the same id as this.datasourceColumnId
            const datasourceColumn =
                contextChange.context.datasourceColumns.find((item) => {
                    return item.id === this.datasourceColumnId;
                });
            // set provided state or default state
            const state = datasourceColumn
                ? datasourceColumn.state
                : getDefaultState();
            this.setupModelFromState(state);
            this.cd.detectChanges();
        }
    };

    /**
     * Setup model from state
     * @param state - State of the column
     * @throws - Error if state is undefined
     */
    private setupModelFromState(state: IMultipleSelectDatasourceColumnState) {
        this.state = state;
        this.selectedOptions = this.state.value.options;
        // Assume that state cannot select all values
        this.selectedOptionsViewModel = this.state.value.options;
        this.previousSelectedViewOptionsModel = this.state.value.options;

        this.selectedFilterTypeModel = this.state.value.filterType;
        this.excludeChecked = this.selectedFilterTypeModel == 'exclude';
        this.splitChecked = this.state.isSplitApplied;
    }

    ngOnInit(): void {
        // console.log('MultipleSelectComponent.ngOnInit');
        // console.log({ initialState: JSON.stringify(this.state) });
        if (this.state) {
            // console.log('Set initial state');
            this.selectedOptions = this.state.value.options;
            this.selectedOptionsViewModel = this.state.value.options;
            this.previousSelectedViewOptionsModel = this.state.value.options;
        }

        // load the initial selectOptions list
        if (this._dataLoaderData.data) {
            this.availableOptions = this._dataLoaderData.data.slice();
            this.availableViewOptions =
                this.availableOptions.length > 0
                    ? [ALL_VALUES_OPTION, ...this._dataLoaderData.data.slice()]
                    : [];
        }

        this.queryContextService.appliedQueryContextEmitter.subscribe(
            this.queryContextSubscriber
        );
    }

    onSplitChange(val: boolean) {
        console.log({
            msg: 'MultipleSelectComponent.onSplitChange',
            filterId: this.datasourceColumnId,
            newValue: val,
        });
        this.state = { ...this.state, isSplitApplied: val };
        const change: IQueryContextChange = {
            datasourceColumn: {
                id: this.datasourceColumnId,
                state: this.state,
            },
        };
        // this.change.emit(change);
        this.queryContextService.changePendingQueryContext(change);
    }

    /**
     * Clears all selection view states. Notifies of the change.
     */
    private clearSelection(): void {
        this.allValuesSelected = false;
        this.selectedOptions = [];
        this.selectedOptionsViewModel = [];
        this.previousSelectedViewOptionsModel = [];
        this.onSelectedOptionsChange([]);
    }

    /**
     * Selects all available options. Notifies of the change.
     */
    private allSelection(): void {
        this.allValuesSelected = true;
        // select all options means that filter is disabled
        this.selectedOptions = [];
        this.selectedOptionsViewModel = this.previousSelectedViewOptionsModel =
            this.availableViewOptions.map((o) => o.value);
        this.onSelectedOptionsChange([]);
    }

    /**
     * Selects an option by its index.
     * @param position - Index of the option to select.
     */
    private setSelectedOption(position: number): void {
        if (position < 0 || position >= this.availableOptions.length) return;

        const value = this.availableOptions[position].value;

        this.selectedOptions = [value];
        this.selectedOptionsViewModel = [value];

        this.onSelectedOptionsChange(this.selectedOptions);
    }

    /**
     * Returns the index of the currently selected option.
     * @returns The option index, or -1 if none is selected.
     */
    private currentOptionIndex(): number {
        if (!this.selectedOptions || this.selectedOptions.length !== 1)
            return -1;
        const current = this.selectedOptions[0];
        return this.availableOptions.findIndex((o) => o.value === current);
    }

    /**
     * Selects the next available option.
     */
    async nextSelectionOption(): Promise<void> {
        if (!this.availableOptions?.length) return;

        const i = this.currentOptionIndex();
        if (i < this.availableOptions.length - 1) {
            this.setSelectedOption(i + 1);
        }
    }

    /**
     * Selects the previous available option.
     */
    async previousSelectionOption(): Promise<void> {
        if (!this.availableOptions?.length) return;

        const i = this.currentOptionIndex();
        if (i > 0) {
            this.setSelectedOption(i - 1);
        }
    }

    /**
     * Detects whether an option has been added or removed based on the previous and current options.
     * @param current - The current set of selected options.
     * @param previous - The old set of selected options.
     * @returns An object representing the change type and value, or undefined if no change is detected.
     */
    private detectChange(current: any[], previous: any[]): OptionChange {
        const hasAdded = current.length > previous.length;
        const hasRemoved = current.length < previous.length;
        const hasReplaced = (() => {
            if (this.selectMode == 'single') {
                if (current.length === 1 && previous.length === 1) {
                    return true;
                }
                if (current.length !== 1 && previous.length === 0) {
                    return false;
                }
                throw new Error(
                    'Single mode selected and current or previous length !== 1 or !== 0.Should not happen'
                );
            }
            return null;
        })();

        if (hasAdded) {
            // Find the added option
            const added = current.find((option) => !previous.includes(option));
            return { changeType: 'add', value: added };
        } else if (hasRemoved) {
            // Find the removed option
            const removed = previous.find(
                (option) => !current.includes(option)
            );
            return { changeType: 'remove', value: removed };
        } else if (hasReplaced) {
            // Find the replaced option
            const replaced = current[0];
            return { changeType: 'replace', value: replaced };
        }

        return undefined;
    }

    /**
     * Handles the special case where the 'All' option is selected or deselected.
     * @param change - The change detected in the options.
     * @returns A boolean indicating whether the 'All' option change was handled.
     */
    private handleAllOptionChange(change: OptionChange): boolean {
        if (change === undefined) {
            throw new Error('Change is undefined');
        }
        if (change.value !== ALL_VALUES_OPTION.value) {
            return false;
        }

        // Set the allValuesSelected flag based on the change type
        this.allValuesSelected = change.changeType === 'add';
        if (this.allValuesSelected) {
            this.allSelection();
        } else {
            this.clearSelection();
        }

        return true;
    }

    /**
     * Handles changes in the selected view options.
     * This method is invoked whenever the selected options change.
     * It updates the internal state to reflect the current selection,
     * and emits an event to notify other parts of the system about the change.
     * @param options - The current list of selected options.
     */
    onSelectedViewOptionsModelChange(options: string[] | string): void {
        if (typeof options === 'string') {
            options = [options];
        }

        if (options.length === 0) {
            this.clearSelection();
            return;
        }

        const change = this.detectChange(
            options,
            this.previousSelectedViewOptionsModel
        );
        if (!change) {
            throw new Error('Change detection failed');
        }

        this.previousSelectedViewOptionsModel = options.slice();

        if (this.handleAllOptionChange(change)) {
            return;
        }

        switch (change.changeType) {
            case 'add':
                this.selectedOptions.push(change.value);
                this.allValuesSelected =
                    this.selectedOptions.length ===
                    this.availableOptions.length;
                if (this.allValuesSelected) {
                    this.allSelection();
                    return;
                }
                this.selectedOptionsViewModel =
                    this.previousSelectedViewOptionsModel =
                        this.selectedOptions.slice();
                break;
            case 'remove':
                if (this.allValuesSelected) {
                    this.selectedOptions = this.availableOptions.map(
                        (o) => o.value
                    );
                }
                const index = this.selectedOptions.indexOf(change.value);
                if (index !== -1) {
                    this.selectedOptions.splice(index, 1);
                }
                this.allValuesSelected = false;
                this.selectedOptionsViewModel =
                    this.previousSelectedViewOptionsModel =
                        this.selectedOptions.slice();
                break;
            case 'replace':
                this.selectedOptions = [change.value];
                this.selectedOptionsViewModel =
                    this.previousSelectedViewOptionsModel =
                        this.selectedOptions.slice();
                break;
        }

        this.onSelectedOptionsChange(this.selectedOptions);
    }

    onSelectedOptionsChange(changes: any): void {
        // UBI-326 Disabled condition
        // ignore if opened
        // if (this.isOpened) {
        //     console.log(
        //         `MultipleSelectComponent.${this.datasourceColumnId}.onModelChange. and dropdown is opened. Ignoring`
        //     );
        //     return;
        // }
        console.log({
            msg: `MultipleSelectComponent.${this.datasourceColumnId}.onModelChange`,
            selectedOptions: this.selectedOptions,
        });
        this.emitState();
    }

    onSelectedFilterTypeModelChange(changes: any): void {
        console.log({
            msg: `MultipleSelectComponent.${this.datasourceColumnId}.onSelectedFilterTypeModelChange`,
            selectedFilterTypeModel: this.selectedFilterTypeModel,
        });
    }

    onFilterTypeCheckboxModelChange(isFilterTypeExclude: any) {
        console.log({
            msg: `MultipleSelectComponent.${this.datasourceColumnId}.onFilterTypeCheckboxChange`,
            isFilterTypeExclude,
        });
        this.selectedFilterTypeModel = isFilterTypeExclude
            ? 'exclude'
            : 'include';
        this.emitState();
    }

    onOpenedChange(isOpened: boolean) {
        this.isOpened = isOpened;
        if (isOpened) {
            // setup with current value
            this.selectedBeforeDropdownOpen = this.selectedOptions.slice();
            return;
        }
        // console.log({
        //     msg: 'MultipleSelectComponent.onOpenedChange',
        //     oldSelectOptions: this.selectedBeforeDropdownOpen.slice(),
        //     // selectOptionsCtrl: this.selectedOptionsCtrl.value?.slice(),
        // });
        //
        if (
            this.selectedOptions &&
            !compareArrays(
                this.selectedOptions,
                this.selectedBeforeDropdownOpen
            )
        )
            this.emitState();
    }

    emitState() {
        this.state = {
            ...this.state,
            value: {
                options: [...this.selectedOptions],
                filterType: this.selectedFilterTypeModel,
            },
            isFilterApplied: MultipleSelectComponent.canFilterApply({
                options: [...this.selectedOptions],
                filterType: this.selectedFilterTypeModel,
            }),
        };
        console.log({
            msg: `MultipleSelectComponent.${this.datasourceColumnId}.emitState`,
            filter: JSON.stringify({
                dimensionId: this.datasourceColumnId,
                state: this.state,
            }),
        });
        const change: IQueryContextChange = {
            datasourceColumn: {
                id: this.datasourceColumnId,
                state: this.state,
            },
        };
        // this.change.emit(change);
        this.queryContextService.changePendingQueryContext(change);
    }

    stateToString() {
        console.log(JSON.stringify(this.state));
    }

    /**
     * Return an SQL string for number or string column and its state
     * @param columnName - Name of the column
     * @param state - State of the column
     * @returns - SQL string
     * @throws - Error if columnName is undefined
     * @throws - Error if state is undefined
     * @throws - Error if state.value is undefined
     * @throws - Error if state.value is not an array
     **/
    public static toDefaultSQL(
        columnName: string,
        state: IMultipleSelectDatasourceColumnState
    ): string {
        // data can be undefined
        // TODO NULL support

        if (typeof columnName == 'undefined') {
            console.log('WTF');
            throw 'Where I am?';
        }
        if (state) {
            const sqlParts = [];
            const NOT = state.value.filterType == 'include' ? '' : 'NOT';
            const isNullPresent =
                state.value.options.filter((val: any) => {
                    return val == null;
                }).length >= 1;
            if (isNullPresent) {
                sqlParts.push(`${columnName} IS NULL`);
            }

            let quote = state.value.options
                // filter out nulls but not falsy values
                .filter((val: any) => val !== null)
                .map((val: any) => {
                    // quote strings
                    if (typeof val == 'string') return `'${val}'`;
                    if (typeof val == 'number') return `${val}`;
                    // quote if other type
                    return `'${val}'`;
                });
            if (quote.length != 0) {
                sqlParts.push(`${columnName} IN (${quote.join(', ')})`);
            }
            return `${NOT} (${sqlParts.join(' OR ')})`;
        }
        return `ERROR. DATA IS UNDEFINED. MultipleSelectComponent ${columnName} ${state}`;
    }

    /**
     * Return an SQL string for number column and its state
     * @param columnName - Name of the column
     * @param state - State of the column
     * @returns - SQL string
     * @throws - Error if columnName is undefined
     * @throws - Error if data is undefined
     * @throws - Error if data.value is undefined
     * @throws - Error if data.value is not an array
     */
    public static toNumberSQL(
        columnName: string,
        state: IMultipleSelectDatasourceColumnState
    ): string {
        // data can be undefined
        // Check if columnName is defined
        if (typeof columnName == 'undefined') {
            console.log('WTF');
            throw 'Where I am?';
        }

        // If data is present
        if (state) {
            const sqlParts = [];
            const NOT = state.value.filterType == 'include' ? '' : 'NOT';
            const isNullPresent =
                state.value.options.filter((val: any) => {
                    return val == null;
                }).length == 1;

            // If null is present in the state, add a "IS NULL" condition to the SQL string
            if (isNullPresent) {
                sqlParts.push(`${columnName} IS NULL`);
            }

            // If non-null values are present, add an "IN" condition to the SQL string
            let quote = state.value.options
                // filter out only nulls, allow 0
                .filter((val: any) => val || val === 0)
                .map((val: any) => `${val}`);
            if (quote.length > 0) {
                sqlParts.push(`${columnName} IN (${quote.join(', ')})`);
            }

            // Return the final SQL string
            return `${NOT} (${sqlParts.join(' OR ')})`;
        }

        // If data is not present, return an error message
        return `ERROR. DATA IS UNDEFINED. MultipleSelectComponent ${columnName} ${state}`;
    }

    public static toBooleanSQL(
        columnName: string,
        state: IMultipleSelectDatasourceColumnState
    ): string {
        // data can be undefined
        // TODO NULL support

        if (typeof columnName == 'undefined') {
            console.log('WTF');
            throw 'Where I am?';
        }
        if (state) {
            const sqlParts = [];
            const NOT = state.value.filterType == 'include' ? '' : 'NOT';
            const isNullPresent =
                state.value.options.filter((val: any) => {
                    return val == null;
                }).length == 1;
            if (isNullPresent) {
                sqlParts.push(`${columnName} IS NULL`);
            }

            console.log({ msg: 'toBooleanSQL', value: state.value });
            let quote = state.value.options
                //filter other than boolean
                .filter((val: any) => typeof val == 'boolean')
                // map ie. columnName IS TRUE
                .map((val: any) => `${columnName} IS ${val}`);
            sqlParts.push(quote);
            return `${NOT} (${sqlParts.join(' OR ')})`;
        }
        return `ERROR. DATA IS UNDEFINED. MultipleSelectComponent ${columnName} ${state}`;
    }

    public static canFilterApply(
        value: IMultipleSelectDatasourceColumnState['value']
    ) {
        if (value.options.length > 0) {
            return true;
        }
        return false;
    }

    public selectedValuesPill() {
        if (this.allValuesSelected === true) {
            return 'All values';
        }
        return selectedValuesPill(
            this.selectedOptions,
            this.availableOptions.map((x) => x.value)
        );
    }

    public getColumnLabel() {
        return (
            getDatasourceColumnName(this.datasourceColumnId) ??
            this.datasourceColumnId
        );
    }
}
